<script setup lang="ts">
import { Link, useForm } from "@inertiajs/vue3"
import { computed } from "vue"
import { UiButton, UiFormLayout, UiSelect, UiTextInput } from "@/components"
import { useNotifications } from "@/composables/useNotifications"

type ProductionOrderItemPayload = {
  id?: number | null
  product_id: number | ""
  quantity: string
  _destroy?: boolean
}

type ProductionOrderPayload = {
  id?: number | null
  company_id: number | ""
  expected_at: string
  items: ProductionOrderItemPayload[]
}

const props = defineProps<{
  mode: "create" | "edit"
  title: string
  subtitle?: string
  productionOrder: ProductionOrderPayload
  products: Array<{ label: string; value: number }>
  errors?: Record<string, string[]>
}>()

const form = useForm({
  production_order: {
    company_id: props.productionOrder.company_id,
    expected_at: props.productionOrder.expected_at,
    items_attributes: props.productionOrder.items.map((item) => ({ ...item, _destroy: false })),
  },
})

const notify = useNotifications()
const visibleItems = computed(() => form.production_order.items_attributes.filter((item) => !item._destroy))
const fieldError = (field: string) => props.errors?.[field]?.[0]

function itemError(index: number, field: string) {
  return props.errors?.[`production_order_items.${index}.${field}`]?.[0] || props.errors?.[`items_attributes.${index}.${field}`]?.[0]
}

function addItem() {
  form.production_order.items_attributes.push({
    id: null,
    product_id: "",
    quantity: "1",
    _destroy: false,
  })
}

function removeItem(index: number) {
  const item = form.production_order.items_attributes[index]
  if (!item) return

  if (item.id) {
    item._destroy = true
    return
  }

  form.production_order.items_attributes.splice(index, 1)
}

function submit() {
  const opts = { onSuccess: () => notify.success(props.mode === "create" ? "Ordem criada com sucesso" : "Ordem atualizada com sucesso") }

  if (props.mode === "create") {
    form.post("/production_orders", opts)
    return
  }

  form.put(`/production_orders/${props.productionOrder.id}`, opts)
}
</script>

<template>
  <UiFormLayout :title="title" :subtitle="subtitle" back-href="/production_orders" back-label="Voltar para ordens de producao">
    <form class="space-y-4" @submit.prevent="submit">
      <div class="grid gap-4 md:grid-cols-2">
        <UiTextInput
          id="production-order-expected-at"
          v-model="form.production_order.expected_at"
          type="date"
          label="Previsao de conclusao"
          :error="fieldError('expected_at')"
        />
      </div>

      <section class="rounded-md border border-slate-200 p-4">
        <div class="mb-3 flex items-center justify-between">
          <h2 class="text-sm font-semibold text-slate-800">Itens da ordem</h2>
          <UiButton type="button" size="sm" variant="secondary" @click="addItem">Adicionar item</UiButton>
        </div>

        <div class="space-y-3">
          <div
            v-for="(item, index) in form.production_order.items_attributes"
            v-show="!item._destroy"
            :key="index"
            class="grid gap-3 rounded-md border border-slate-100 p-3 md:grid-cols-5"
          >
            <div class="md:col-span-3">
              <UiSelect
                :id="`production-order-item-product-${index}`"
                :model-value="item.product_id"
                label="Produto"
                :options="products"
                :error="itemError(index, 'product_id')"
                @update:model-value="item.product_id = $event as number | ''"
              />
            </div>

            <UiTextInput
              :id="`production-order-item-quantity-${index}`"
              v-model="item.quantity"
              type="number"
              label="Quantidade"
              :error="itemError(index, 'quantity')"
            />

            <div class="flex items-end">
              <UiButton type="button" size="sm" variant="danger" @click="removeItem(index)">Remover</UiButton>
            </div>
          </div>

          <p v-if="visibleItems.length === 0" class="text-sm text-slate-500">Adicione ao menos um item de producao.</p>
          <p v-if="fieldError('base')" class="text-sm text-rose-600">{{ fieldError('base') }}</p>
        </div>
      </section>

      <div class="flex items-center gap-3 pt-2">
        <UiButton type="submit" :disabled="form.processing">
          {{ mode === "create" ? "Criar ordem" : "Salvar alteracoes" }}
        </UiButton>

        <Link href="/production_orders" class="rounded-md border border-slate-300 px-4 py-2 text-sm font-medium text-slate-700 transition hover:bg-slate-100">
          Cancelar
        </Link>
      </div>
    </form>
  </UiFormLayout>
</template>

